// ═══════════════════════════════════════════════════════════════
// Intelligens Fájlnév-elemző és Pont-illesztő Segédmodul
// ═══════════════════════════════════════════════════════════════

import type { RecordItem } from '../types';

export interface ParsedFileInfo {
  file: File;
  originalName: string;
  fileSizeFormatted: string;
  detectedPoint: string | null;
  detectedYear: string | null;
  cleanTitle: string;
  matchedRecordId: number | null;
  matchedRecordLabel: string | null;
  confidence: 'high' | 'medium' | 'low' | 'none';
}

/**
 * Bájtok formázása olvasható szöveggé (pl. 2.4 MB)
 */
export function formatBytes(bytes: number, decimals = 1): string {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Normalizálja a pontszámot összehasonlításhoz:
 * pl. "3.2.1." -> "3.2.1", " 1. pont " -> "1", "3_2_1" -> "3.2.1"
 */
export function normalizePoint(raw: string | null | undefined): string {
  if (!raw) return '';
  let s = String(raw).trim().toLowerCase();
  // Kiszűrjük a "pont", "bekezdés" szavakat és zárójeleket
  s = s.replace(/pont|bek|bekezdés/g, '').trim();
  // 3_2_1 -> 3.2.1
  s = s.replace(/_/g, '.');
  // Kettős vagy felesleges pontok és whitespace eltávolítása
  s = s.replace(/\.+/g, '.').replace(/^\.+|\.+$/g, '').trim();
  // vezető nullák szegmensenként: „1.08” = „1.8”, „01” = „1” (a 2026-os lista és a fájlnév-konvenció eltérhet)
  s = s.split('.').map((seg) => (/^\d+$/.test(seg) ? String(Number(seg)) : seg)).join('.');
  return s;
}

/** a pont fő része (alpont-csoport nélkül): „1.3 › Vezetők” → „1.3” */
export function mainPoint(raw: string | null | undefined): string {
  return normalizePoint(String(raw ?? '').split('›')[0]);
}

/**
 * Fájlnév elemzése: pontszám, évszám és tiszta cím kinyerése
 */
export function parseFileName(file: File): {
  detectedPoint: string | null;
  detectedYear: string | null;
  cleanTitle: string;
} {
  const name = file.name;
  const baseName = name.replace(/\.[^/.]+$/, ''); // kiterjesztés levágása

  let detectedPoint: string | null = null;
  let detectedYear: string | null = null;

  // 1. Évszám keresése (1980 - 2040)
  const yearMatch = baseName.match(/(?<!\d)(19[89]\d|20[0-4]\d)(?!\d)/);
  if (yearMatch) {
    detectedYear = yearMatch[1];
  }

  // 2. Pontszám minták tesztelése
  // A) Prefix számozott hierarchia: "3.2.1_...", "1.6.2-...", "3_2_1_..."
  const prefixMatch = baseName.match(/^(\d+(?:[._]\d+)*)[_.\-\s]/);
  let rawPrefix: string | null = null;
  if (prefixMatch) {
    const candidate = normalizePoint(prefixMatch[1]);
    // „00_Tartalomjegyzek…” – a 0 nem közzétételi pont
    if (candidate !== detectedYear && !/^(19\d\d|20\d\d)$/.test(candidate) && candidate !== '0') {
      detectedPoint = candidate;
      rawPrefix = prefixMatch[1];
    }
  }

  // B) "pont_4_...", "pont-1.2_...", "pont4_..."
  if (!detectedPoint) {
    const pontPrefixMatch = baseName.match(/^[_\-\s]?pont[_\-\s]?(\d+(?:[._]\d+)*)/i);
    if (pontPrefixMatch) {
      detectedPoint = normalizePoint(pontPrefixMatch[1]);
    }
  }

  // C) "1_pont_...", "5-pont_...", "3.2-pont_..."
  if (!detectedPoint) {
    const numPontMatch = baseName.match(/(\d+(?:[._]\d+)*)[_\-\s]?pont/i);
    if (numPontMatch) {
      detectedPoint = normalizePoint(numPontMatch[1]);
    }
  }

  // D) Ha a névben bárhol előfordul pontszám: pl. "alapito_okirat_3.2.1.pdf"
  if (!detectedPoint) {
    const infixMatch = baseName.match(/[_\-\s](\d+(?:\.\d+)+)[_\-\s.]?/);
    if (infixMatch) {
      const candidate = normalizePoint(infixMatch[1]);
      if (!/^\d{4}\.\d{2}\.\d{2}$/.test(candidate)) {
        detectedPoint = candidate;
      }
    }
  }

  // 3. Tiszta megnevezés előállítása (felesleges technikai részek nélkül)
  let cleanTitle = baseName;
  if (rawPrefix) cleanTitle = cleanTitle.slice(rawPrefix.length);
  if (detectedPoint) {
    // Levágjuk a felismert pontot a címből
    cleanTitle = cleanTitle
      .replace(new RegExp(`^${detectedPoint.replace(/\./g, '[._]')}[_\\-\\s]*`, 'i'), '')
      .replace(new RegExp(`[\\-_\\s]*${detectedPoint.replace(/\./g, '[._]')}[\\-_\\s]*`, 'i'), ' ')
      .replace(/pont[_\-\s]?\d+/gi, '')
      .replace(/\d+[_\-\s]?pont/gi, '');
  }
  // Tisztítás: aláhúzások, kötőjelek szóközre cserélése
  cleanTitle = cleanTitle.replace(/[_\-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!cleanTitle) cleanTitle = baseName;

  return { detectedPoint, detectedYear, cleanTitle };
}

/**
 * Rekordokhoz való intelligens illesztés
 */
export function matchFileToRecords(
  file: File,
  records: RecordItem[]
): ParsedFileInfo {
  const { detectedPoint, detectedYear, cleanTitle } = parseFileName(file);
  const sizeFormatted = formatBytes(file.size);

  if (!records || records.length === 0) {
    return {
      file,
      originalName: file.name,
      fileSizeFormatted: sizeFormatted,
      detectedPoint,
      detectedYear,
      cleanTitle,
      matchedRecordId: null,
      matchedRecordLabel: null,
      confidence: 'none',
    };
  }

  let matchedRecord: RecordItem | null = null;
  let confidence: 'high' | 'medium' | 'low' | 'none' = 'none';

  // 1. Pontszám alapú illesztés (szegmensenként, vezető nullák nélkül):
  //    a pontosan egyező főpont nyer; ha csak alpont-csoport (pl. „1.3 › Vezetők”) egyezik, az közepes
  let matchedExact = false;
  if (detectedPoint) {
    for (const rec of records) {
      const d = rec.parsedData || (typeof rec.data === 'string' ? JSON.parse(rec.data || '{}') : rec.data) || {};
      const raw = String(d.pont || '');
      if (!raw.trim()) continue;
      const isSub = raw.includes('›');
      const recPoint = mainPoint(raw);
      if (recPoint !== detectedPoint) continue;
      if (!isSub) {
        // több egyező bejegyzés (pl. a sablonbeli „1.8” és egy korábbi „1.08.”): a pontosan így írt / sablonból
        // származó a nyerő
        const exact = raw.trim().replace(/\.+$/, '') === detectedPoint || Boolean(d.sablon_pont);
        if (confidence !== 'high' || (exact && !matchedExact)) {
          matchedRecord = rec;
          matchedExact = exact;
          confidence = 'high';
          if (exact) break;
        }
        continue;
      }
      if (!matchedRecord) { matchedRecord = rec; confidence = 'medium'; }
    }
  }

  // 2. Ha pont alapján nem találtunk, de van évszám és a rekordban is szerepel az év
  if (!matchedRecord && detectedYear) {
    const yearMatches = records.filter(rec => {
      const d = rec.parsedData || (typeof rec.data === 'string' ? JSON.parse(rec.data || '{}') : rec.data);
      const str = JSON.stringify(d);
      return str.includes(detectedYear!);
    });
    if (yearMatches.length === 1) {
      matchedRecord = yearMatches[0];
      confidence = 'low';
    }
  }

  let matchedRecordLabel: string | null = null;
  if (matchedRecord) {
    const d = matchedRecord.parsedData || (typeof matchedRecord.data === 'string' ? JSON.parse(matchedRecord.data || '{}') : matchedRecord.data);
    const p = d.pont ? `${d.pont} ` : '';
    const title = d.megnevezes || d.cim || d.nev || `Rekord #${matchedRecord.id}`;
    matchedRecordLabel = `${p}${title}`.trim();
  }

  return {
    file,
    originalName: file.name,
    fileSizeFormatted: sizeFormatted,
    detectedPoint,
    detectedYear,
    cleanTitle,
    matchedRecordId: matchedRecord ? matchedRecord.id : null,
    matchedRecordLabel,
    confidence,
  };
}
